import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { isEnglishRoute } from '../lib/i18n';
import { fetchLocalizedCollection } from '../lib/cms';
import { useNavigationMenu, getMenuHref, getMenuTitle } from '../lib/useNavigationMenu';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const isEn = isEnglishRoute(location.pathname);
  const [companyInfo, setCompanyInfo] = useState(null);
  const { productItems, aboutItems } = useNavigationMenu();

  useEffect(() => {
    fetchLocalizedCollection('virksomhedsoplysninger', isEn ? 'en' : 'da').then((items) => {
      if (items && items.length) {
        setCompanyInfo(items[0]);
      }
    });
  }, [isEn]);

  // Localized URLs
  const contactUrl = isEn ? '/en/contact' : '/kontakt';
  const termsUrl = isEn ? '/en/terms-and-conditions' : '/pmb-salgs-leveringsbetingelser';
  const aboutUrl = isEn ? '/en/about' : '/om-os';
  const suppliersUrl = isEn ? '/en/suppliers' : '/leverandoerer';
  const partnersUrl = isEn ? '/en/partners' : '/pmb-partnere';
  const jobsUrl = isEn ? '/en/jobs' : '/pmb-job-ledige-stillinger';
  const sparePartsUrl = isEn ? '/en/spare-parts' : '/reservedele';
  const laserUrl = isEn ? '/en/laser' : '/laser';
  const shearsUrl = isEn ? '/en/shears' : '/saks';
  const pressBrakesUrl = isEn ? '/en/press-brakes' : '/kantpresser';
  const equipmentUrl = isEn ? '/en/equipment' : '/udstyr';
  const pressesUrl = isEn ? '/en/presses' : '/presser';
  const usedPressesUrl = isEn ? '/en/used-presses' : '/presser-2';
  const lubricationUrl = isEn ? '/en/central-lubrication' : '/centralsmoering';
  const serviceUrl = isEn ? '/en/service' : '/service';

  return (
    <footer className="bg-[#1a2024] text-stone-300 border-t border-stone-800">
      {/* Top CTA Banner */}
      <div className="bg-[#006368] text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
              {isEn
                ? 'Ready to optimize your production?'
                : 'Klar til at optimere jeres produktion?'}
            </h2>
            <p className="text-white/80 mt-2 text-sm sm:text-base max-w-2xl">
              {isEn
                ? 'Get advice from specialists with over 100 years of combined experience in presses, coil lines, and centralized lubrication.'
                : 'Få rådgivning fra specialister med mere end 100 års samlet erfaring inden for presser, coil-linjer og centralsmøring.'}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
            <a
              href="tel:57526262"
              className="px-6 py-3.5 rounded bg-white text-[#006368] font-bold text-sm hover:bg-stone-100 transition shadow-sm whitespace-nowrap flex items-center gap-2"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span className="whitespace-nowrap">
                {isEn ? 'Call +45 57 52 62 62' : 'Ring 57 52 62 62'}
              </span>
            </a>
            <Link
              to={contactUrl}
              className="px-6 py-3.5 rounded border border-white/30 text-white font-semibold text-sm hover:bg-white/10 transition whitespace-nowrap flex items-center gap-2"
            >
              <span>{isEn ? 'Get a quote' : 'Få et tilbud'}</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10">
          {/* Company Bio */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center">
              <div className="bg-white px-3 py-2 rounded inline-flex items-center justify-center shadow-sm">
                <img
                  src={companyInfo?.logo?.[0] || "https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/pmb-logo.png"}
                  alt={companyInfo?.firmanavn || "POVL MØLLERS MASKINFABRIK A/S"}
                  className="h-10 w-auto object-contain"
                  width={130}
                  height={40}
                />
              </div>
            </div>
            <p className="text-sm text-stone-400 leading-relaxed pr-4">
              {companyInfo?.kort_beskrivelse || (isEn
                ? 'Danish machine factory since 1921. We deliver power, precision, and operational reliability through new and refurbished presses, cutting, bending, auxiliary equipment, and nationwide centralized lubrication.'
                : 'Dansk maskinfabrik siden 1921. Vi leverer kraft, præcision og driftssikkerhed gennem nye og renoverede presser, skæring, bukning, følgeudstyr og landsdækkende centralsmøring.')}
            </p>
            <div className="pt-2 text-xs text-stone-400 space-y-1.5">
              <p>
                CVR:{' '}
                <strong className="text-stone-200">{companyInfo?.cvr || '52043018'}</strong>
              </p>
              <p>
                {isEn ? 'Established: ' : 'Etableret: '}
                <strong className="text-stone-200">
                  {isEn ? '1921 in Borup, Denmark' : '1921 i Borup'}
                </strong>
              </p>
            </div>
          </div>

          {/* Produkter / Products */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-white mb-4">
              {isEn ? 'Products' : 'Produkter'}
            </h3>
            <ul className="space-y-2.5 text-sm">
              {productItems.map((item) => (
                <li key={item.id}>
                  <Link to={getMenuHref(item.sti, isEn)} className="hover:text-white transition">
                    {getMenuTitle(item, isEn)}
                  </Link>
                </li>
              ))}
            </ul>

            <h3 className="text-xs font-bold uppercase tracking-wider text-white mt-6 mb-3">
              {isEn ? 'Lubrication & Service' : 'Smøring & Service'}
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link
                  to={lubricationUrl}
                  className="hover:text-white transition text-[#0d8086] font-medium"
                >
                  {isEn ? 'Central Lubrication' : 'Centralsmøring'}
                </Link>
              </li>
              <li>
                <Link
                  to={serviceUrl}
                  className="hover:text-white transition text-[#0d8086] font-medium"
                >
                  {isEn ? 'Service & Repair' : 'Service & Reparation'}
                </Link>
              </li>
            </ul>
          </div>

          {/* Virksomheden / Company */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-white mb-4">
              {isEn ? 'Company' : 'Virksomheden'}
            </h3>
            <ul className="space-y-2.5 text-sm">
              {aboutItems.map((item) => (
                <li key={item.id}>
                  <Link to={getMenuHref(item.sti, isEn)} className="hover:text-white transition">
                    {getMenuTitle(item, isEn)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Kontaktinfo / Contact */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-white mb-4">
              {isEn ? 'Contact' : 'Kontakt'}
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <i className="fa-solid fa-location-dot text-[#0d8086] mt-1 shrink-0" />
                <span>
                  {companyInfo?.adresse || 'Kimmerslevvej 3'}<br />
                  {companyInfo?.postnr_by || '4140 Borup'}<br />
                  {isEn ? 'Denmark' : 'Danmark'}
                </span>
              </div>

              <div className="flex items-center gap-3">
                <i className="fa-solid fa-phone text-[#0d8086] shrink-0" />
                <a
                  href={`tel:${(companyInfo?.telefon || '57526262').replace(/\s+/g, '')}`}
                  className="hover:text-white transition whitespace-nowrap font-medium"
                >
                  {companyInfo?.telefon || (isEn ? '+45 57 52 62 62' : '57 52 62 62')}
                </a>
              </div>

              <div className="flex items-center gap-3">
                <i className="fa-solid fa-envelope text-[#0d8086] shrink-0" />
                <a
                  href={`mailto:${companyInfo?.email || 'info@pmborup.dk'}`}
                  className="hover:text-white transition whitespace-nowrap"
                >
                  {companyInfo?.email || 'info@pmborup.dk'}
                </a>
              </div>

              {companyInfo?.aabningstider && (
                <div className="flex items-start gap-3 pt-1 text-xs text-stone-400">
                  <i className="fa-regular fa-clock text-[#0d8086] mt-0.5 shrink-0" />
                  <span>{companyInfo.aabningstider}</span>
                </div>
              )}

              <div className="pt-2">
                <Link
                  to={contactUrl}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-[#0d8086] hover:text-white transition"
                >
                  <span>
                    {isEn ? 'Open contact form' : 'Åbn kontaktformular'}
                  </span>
                  <i className="fa-solid fa-arrow-right text-[10px]" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-stone-800/80 flex flex-col sm:flex-row items-center justify-between text-xs text-stone-400 gap-4">
          <p>
            © {currentYear} POVL MØLLERS MASKINFABRIK A/S.{' '}
            {isEn ? 'All rights reserved.' : 'Alle rettigheder forbeholdes.'}
          </p>
          <div className="flex items-center gap-6">
            <Link to={termsUrl} className="hover:text-stone-300 transition">
              {isEn ? 'Terms & Conditions' : 'Salgsbetingelser'}
            </Link>
            <span>•</span>
            <span className="whitespace-nowrap">CVR: 52043018</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
